import { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, Play, Pause, Square, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { useTimerStore } from '@/store/timerStore';
import { useAuth } from '@/context/AuthContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDatabase } from '@/context/DatabaseContext';

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

export function TimerWidget() {
  const { user } = useAuth();
  const { tasks } = useDatabase();
  const {
    activeTaskId,
    activeTaskName,
    isRunning,
    getElapsedMs,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    resetTimer,
  } = useTimerStore();

  const [elapsed, setElapsed] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useClickOutside([panelRef], () => { setIsOpen(false); setShowPicker(false); }, isOpen);

  // Update elapsed every second when running
  useEffect(() => {
    if (!activeTaskId) { setElapsed(0); return; }
    setElapsed(getElapsedMs());
    if (!isRunning) return;
    const interval = setInterval(() => setElapsed(getElapsedMs()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, activeTaskId, getElapsedMs]);

  // Focus search when picker opens
  useEffect(() => {
    if (showPicker && searchRef.current) searchRef.current.focus();
  }, [showPicker]);

  const handleStop = useCallback(async () => {
    if (!user) return;
    await stopTimer(user.id);
    setIsOpen(false);
  }, [user, stopTimer]);

  const handleDiscard = useCallback(() => {
    resetTimer();
    setIsOpen(false);
  }, [resetTimer]);

  const handleSelectTask = useCallback((taskId: string, taskName: string) => {
    startTimer(taskId, taskName);
    setShowPicker(false);
    setSearch('');
  }, [startTimer]);

  // Filter tasks for picker — show open/in_progress, exclude subtasks for brevity
  const filteredTasks = tasks
    .filter(t =>
      !t.parentId &&
      (t.status === 'open' || t.status === 'in_progress') &&
      (search === '' || t.name.toLowerCase().includes(search.toLowerCase()))
    )
    .slice(0, 15);

  // Compact button when no timer active
  if (!activeTaskId) {
    return (
      <div className="relative" ref={panelRef}>
        <button
          onClick={() => { setIsOpen(!isOpen); setShowPicker(true); }}
          className={clsx(
            'relative p-1.5 rounded-md transition-colors',
            isOpen
              ? 'bg-accent-emerald/15 text-accent-emerald'
              : 'text-text-quaternary hover:bg-surface-hover hover:text-text-tertiary',
          )}
          title="Avvia timer"
        >
          <Timer className="w-4 h-4" />
        </button>

        {isOpen && showPicker && (
          <div className="absolute left-0 top-full mt-1 w-64 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in">
            <div className="px-3 py-2 border-b border-border-primary">
              <span className="text-[11px] font-semibold text-text-primary">Avvia timer</span>
            </div>
            <div className="px-2 py-1.5 border-b border-border-primary">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cerca task..."
                className="w-full px-2 py-1 text-[11px] bg-surface-primary border border-border-primary rounded text-text-primary placeholder:text-text-quaternary focus:outline-none focus:border-accent-emerald"
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredTasks.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11px] text-text-quaternary">
                  Nessun task trovato
                </div>
              ) : (
                filteredTasks.map(t => (
                  <button
                    key={t.id}
                    onClick={() => handleSelectTask(t.id, t.name)}
                    className="w-full text-left px-3 py-2 text-[11px] text-text-secondary hover:bg-surface-hover transition-colors truncate"
                  >
                    {t.name}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Active timer display
  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors text-[11px] font-medium',
          isRunning
            ? 'bg-accent-emerald/15 text-accent-emerald'
            : 'bg-accent-amber/15 text-accent-amber',
        )}
        title={activeTaskName || 'Timer'}
      >
        {isRunning && (
          <span className="w-1.5 h-1.5 rounded-full bg-accent-emerald animate-pulse" />
        )}
        <span className="tabular-nums">{formatTime(elapsed)}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-surface-secondary border border-border-primary rounded-lg shadow-xl z-50 overflow-hidden animate-fade-in">
          {/* Header with task name */}
          <div className="px-3 py-2 border-b border-border-primary">
            <div className="text-[10px] uppercase tracking-wider text-text-quaternary mb-0.5">Timer attivo</div>
            <div className="text-[11px] font-medium text-text-primary truncate">{activeTaskName}</div>
          </div>

          {/* Time display */}
          <div className="px-3 py-3 text-center border-b border-border-primary">
            <span className={clsx(
              'text-2xl font-mono font-semibold tabular-nums',
              isRunning ? 'text-accent-emerald' : 'text-accent-amber',
            )}>
              {formatTime(elapsed)}
            </span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-2 px-3 py-2.5">
            {isRunning ? (
              <button
                onClick={pauseTimer}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-accent-amber/15 text-accent-amber hover:bg-accent-amber/25 transition-colors"
              >
                <Pause className="w-3.5 h-3.5" />
                Pausa
              </button>
            ) : (
              <button
                onClick={resumeTimer}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-accent-emerald/15 text-accent-emerald hover:bg-accent-emerald/25 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Riprendi
              </button>
            )}
            <button
              onClick={handleStop}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium bg-accent-violet/15 text-accent-violet hover:bg-accent-violet/25 transition-colors"
              title="Salva e ferma"
            >
              <Square className="w-3.5 h-3.5" />
              Salva
            </button>
            <button
              onClick={handleDiscard}
              className="px-2 py-1.5 rounded-md text-[11px] text-text-quaternary hover:text-accent-rose hover:bg-accent-rose/10 transition-colors"
              title="Scarta senza salvare"
            >
              Scarta
            </button>
          </div>

          {/* Switch task */}
          <div className="border-t border-border-primary">
            <button
              onClick={() => setShowPicker(!showPicker)}
              className="w-full px-3 py-2 text-[10px] text-text-quaternary hover:text-text-secondary hover:bg-surface-hover transition-colors text-left"
            >
              Cambia task...
            </button>
            {showPicker && (
              <>
                <div className="px-2 py-1 border-t border-border-primary">
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Cerca task..."
                    className="w-full px-2 py-1 text-[11px] bg-surface-primary border border-border-primary rounded text-text-primary placeholder:text-text-quaternary focus:outline-none focus:border-accent-emerald"
                  />
                </div>
                <div className="max-h-36 overflow-y-auto">
                  {filteredTasks.map(t => (
                    <button
                      key={t.id}
                      onClick={() => handleSelectTask(t.id, t.name)}
                      className="w-full text-left px-3 py-1.5 text-[11px] text-text-secondary hover:bg-surface-hover transition-colors truncate"
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
